import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import Modal from '@/v2/components/common/Modal'
import TextField from '@/v2/components/common/TextField'
import SelectField from '@/v2/components/common/SelectField'
import CheckboxField from '@/v2/components/common/CheckboxField'
import Typography from '@/v2/components/common/Typography'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import {
  useSettingsClinicUsers,
  useUpdateClinicUser,
  useRevokeClinicUser,
  useReinstateClinicUser,
  useInviteClinicUser,
  useSettingsInvitations,
  useUpdateInvitation,
  useDeleteInvitation,
  useResendInvitation,
} from '@/v2/hooks/settings/useSettingsClinicUsers'
import useFormModal from '@/v2/hooks/settings/useFormModal'
import {
  SettingsPage,
  SettingsEmptyState,
  PrimaryButton,
  SecondaryButton,
  HeaderActions,
  CardGrid,
  Card,
  CardTitle,
  CardActions,
  ActionButton,
} from '@/v2/components/settings/shared'

const UserEmail = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
`

const RoleTags = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
`

const RoleTag = styled.span`
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;

  ${props =>
    props.role === 'doctor'
      ? `
    background-color: #7bcc7d;
    color: white;
  `
      : props.role === 'admin'
      ? `
    background-color: #ed685e;
    color: white;
  `
      : props.role === 'dashboard'
      ? `
    background-color: #a78bfa;
    color: white;
  `
      : `
    background-color: #4c9bda;
    color: white;
  `}
`

const RevokedBadge = styled.div`
  background-color: #fef2f2;
  color: #dc2626;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 0.75rem;
`

const InvitationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`

const InvitationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
`

const InvitationName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
`

const InvitationEmail = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const InvitationMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`

const InvStatusBadge = styled.span`
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;

  ${props =>
    props.status === 'pending'
      ? `
    background-color: #fef3c7;
    color: #92400e;
  `
      : props.status === 'expired'
      ? `
    background-color: #fef2f2;
    color: #dc2626;
  `
      : `
    background-color: #d1fae5;
    color: #065f46;
  `}
`

const InvitationActions = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
  margin-left: 0.75rem;
`

const roleOptions = [
  { label: 'Staff', value: 'staff' },
  { label: 'Doctor', value: 'doctor' },
  { label: 'Admin', value: 'admin' },
  { label: 'Dashboard', value: 'dashboard' },
]

const formatDate = dateString => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const Users = () => {
  const { clinicUsers, isLoading: loading } = useSettingsClinicUsers()
  const { updateClinicUser, isPending: isUpdating } = useUpdateClinicUser()
  const { revokeClinicUser, isPending: isRevoking } = useRevokeClinicUser()
  const {
    reinstateClinicUser,
    isPending: isReinstating,
  } = useReinstateClinicUser()
  const { inviteClinicUser, isPending: isInviting } = useInviteClinicUser()
  const {
    invitations,
    isLoading: loadingInvitations,
  } = useSettingsInvitations()
  const {
    updateInvitation,
    isPending: isUpdatingInvitation,
  } = useUpdateInvitation()
  const {
    deleteInvitation,
    isPending: isDeletingInvitation,
  } = useDeleteInvitation()
  const {
    resendInvitation,
    isPending: isResendingInvitation,
  } = useResendInvitation()
  const { onOpenSnackbar } = useSnackbarContext()

  const users = useMemo(
    () =>
      (clinicUsers || []).map(clinicUser => ({
        id: clinicUser.id,
        userId: clinicUser.user?.id,
        firstName: clinicUser.user?.firstName || '',
        lastName: clinicUser.user?.lastName || '',
        email: clinicUser.user?.email || '',
        roles: clinicUser.roles || ['staff'],
        revoked: clinicUser.revoked || false,
      })),
    [clinicUsers]
  )

  const [showInvitationsModal, setShowInvitationsModal] = useState(false)
  const [showEditInvitationModal, setShowEditInvitationModal] = useState(false)
  const [editingInvitation, setEditingInvitation] = useState(null)

  // Invite modal
  const inviteModal = useFormModal({
    firstName: '',
    lastName: '',
    email: '',
    role: 'staff',
  })

  // Edit user modal
  const editModal = useFormModal({
    firstName: '',
    lastName: '',
    email: '',
    roles: [],
  })

  // Edit invitation form
  const [editInvitationFormData, setEditInvitationFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'staff',
  })

  const handleInviteUser = () => {
    inviteModal.openForCreate()
  }

  const handleEditUser = user => {
    editModal.openForEdit(user, u => ({
      firstName: u.firstName,
      lastName: u.lastName,
      email: u.email,
      roles: u.roles,
    }))
  }

  const handleRevokeAccess = async userId => {
    if (confirm('Are you sure you want to revoke access for this user?')) {
      try {
        await revokeClinicUser(userId)
        onOpenSnackbar('User access revoked successfully', 'success')
      } catch (error) {
        onOpenSnackbar('Failed to revoke user access', 'error')
      }
    }
  }

  const handleReinstateAccess = async userId => {
    if (confirm('Are you sure you want to reinstate access for this user?')) {
      try {
        await reinstateClinicUser(userId)
        onOpenSnackbar('User access reinstated successfully', 'success')
      } catch (error) {
        onOpenSnackbar('Failed to reinstate user access', 'error')
      }
    }
  }

  const handleInviteSubmit = async () => {
    try {
      await inviteClinicUser({
        first_name: inviteModal.formData.firstName,
        last_name: inviteModal.formData.lastName,
        email: inviteModal.formData.email,
        role: inviteModal.formData.role,
      })

      onOpenSnackbar('Invitation sent successfully', 'success')
      inviteModal.close()
    } catch (error) {
      onOpenSnackbar('Failed to send invitation', 'error')
    }
  }

  const handleEditSubmit = async () => {
    try {
      await updateClinicUser({
        id: editModal.editingItem.id,
        user_attributes: {
          id: editModal.editingItem.userId,
          first_name: editModal.formData.firstName,
          last_name: editModal.formData.lastName,
          email: editModal.formData.email,
        },
        roles: editModal.formData.roles,
      })

      onOpenSnackbar('User updated successfully', 'success')
      editModal.close()
    } catch (error) {
      onOpenSnackbar('Failed to update user', 'error')
    }
  }

  const handleRoleToggle = role => {
    editModal.setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role],
    }))
  }

  const handleEditInvitation = invitation => {
    setEditingInvitation(invitation)
    setEditInvitationFormData({
      firstName: invitation.firstName || '',
      lastName: invitation.lastName || '',
      email: invitation.email || '',
      role: invitation.role || 'staff',
    })
    setShowEditInvitationModal(true)
  }

  const handleEditInvitationSubmit = async () => {
    try {
      await updateInvitation({
        id: editingInvitation.id,
        first_name: editInvitationFormData.firstName,
        last_name: editInvitationFormData.lastName,
        email: editInvitationFormData.email,
        role: editInvitationFormData.role,
      })

      onOpenSnackbar('Invitation updated successfully', 'success')
      setShowEditInvitationModal(false)
      setEditingInvitation(null)
    } catch (error) {
      onOpenSnackbar('Failed to update invitation', 'error')
    }
  }

  const handleDeleteInvitation = async invitation => {
    if (
      confirm(
        `Are you sure you want to delete the invitation for ${invitation.firstName} ${invitation.lastName}?`
      )
    ) {
      try {
        await deleteInvitation(invitation.id)
        onOpenSnackbar('Invitation deleted successfully', 'success')
      } catch (error) {
        onOpenSnackbar('Failed to delete invitation', 'error')
      }
    }
  }

  const handleResendInvitation = async invitation => {
    try {
      await resendInvitation(invitation.id)
      onOpenSnackbar('Invitation resent successfully', 'success')
    } catch (error) {
      onOpenSnackbar('Failed to resend invitation', 'error')
    }
  }

  const handleEditInvitationInputChange = (field, value) => {
    setEditInvitationFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <SettingsPage
      title="Manage users"
      subtitle="Invite team members and manage user permissions"
      loading={loading}
      actions={
        <HeaderActions>
          <SecondaryButton onClick={() => setShowInvitationsModal(true)}>
            Invitations Sent
          </SecondaryButton>
          <PrimaryButton onClick={handleInviteUser}>+ Add User</PrimaryButton>
        </HeaderActions>
      }
    >
      {users.length === 0 ? (
        <SettingsEmptyState
          icon={
            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          }
          heading="You haven't added any user yet"
          description={
            <>
              Click on <strong>Add user</strong> to register the first one
            </>
          }
          actionLabel="Add user"
          onAction={handleInviteUser}
        />
      ) : (
        <CardGrid $minWidth="320px">
          {users.map(user => (
            <Card key={user.id} onClick={() => handleEditUser(user)}>
              <CardTitle $mb="0.25rem">
                {user.firstName} {user.lastName}
              </CardTitle>
              <UserEmail>{user.email}</UserEmail>

              <RoleTags>
                {user.roles.map(role => (
                  <RoleTag key={role} role={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </RoleTag>
                ))}
              </RoleTags>

              {user.revoked && <RevokedBadge>ACCESS REVOKED</RevokedBadge>}

              <CardActions onClick={e => e.stopPropagation()}>
                <ActionButton onClick={() => handleEditUser(user)}>
                  Edit
                </ActionButton>
                {user.revoked ? (
                  <ActionButton onClick={() => handleReinstateAccess(user.id)}>
                    Reinstate
                  </ActionButton>
                ) : (
                  <ActionButton
                    $variant="danger"
                    onClick={() => handleRevokeAccess(user.id)}
                  >
                    Revoke
                  </ActionButton>
                )}
              </CardActions>
            </Card>
          ))}
        </CardGrid>
      )}

      <Modal
        isOpen={inviteModal.showModal}
        title="Add User"
        onClose={inviteModal.close}
        onConfirm={handleInviteSubmit}
        confirmLabel="Send Invite"
        cancelLabel="Cancel"
        submitDisabled={isInviting}
        loading={isInviting}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography bold fontSize="1rem">
              User
            </Typography>
            <Typography
              fontSize="0.875rem"
              style={{ color: '#6b7280', marginBottom: '1rem' }}
            >
              Give member access to your clinic by sending them an invitation
            </Typography>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}
            >
              <TextField
                fullWidth
                label="Name"
                value={inviteModal.formData.firstName}
                onChange={val => inviteModal.updateField('firstName', val)}
                placeholder="E.g.: Victor"
              />
              <TextField
                fullWidth
                label="Last Name"
                value={inviteModal.formData.lastName}
                onChange={val => inviteModal.updateField('lastName', val)}
                placeholder="E.g.: Frankenstein"
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={inviteModal.formData.email}
                onChange={val => inviteModal.updateField('email', val)}
                placeholder="E.g.: example@clinic.com"
              />
            </Box>
          </Box>

          <Box>
            <Typography bold fontSize="1rem">
              Roles & Permissions
            </Typography>
            <Typography
              fontSize="0.875rem"
              style={{ color: '#6b7280', marginBottom: '1rem' }}
            >
              Choose which role the new member will have on your clinic
            </Typography>
            <SelectField
              fullWidth
              label="Role"
              options={roleOptions}
              value={inviteModal.formData.role}
              onChange={val => inviteModal.updateField('role', val)}
            />
          </Box>
        </Box>
      </Modal>

      <Modal
        isOpen={editModal.showModal}
        title="Edit User"
        onClose={editModal.close}
        onConfirm={handleEditSubmit}
        confirmLabel="Save Changes"
        cancelLabel="Cancel"
        submitDisabled={isUpdating}
        loading={isUpdating}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography bold fontSize="1rem">
              User Information
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
                mt: 1,
              }}
            >
              <TextField
                fullWidth
                label="Name"
                value={editModal.formData.firstName}
                onChange={val => editModal.updateField('firstName', val)}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={editModal.formData.lastName}
                onChange={val => editModal.updateField('lastName', val)}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editModal.formData.email}
                onChange={val => editModal.updateField('email', val)}
              />
            </Box>
          </Box>

          <Box>
            <Typography bold fontSize="1rem">
              Roles & Permissions
            </Typography>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}
            >
              {['staff', 'doctor', 'admin', 'dashboard'].map(role => (
                <CheckboxField
                  key={role}
                  label={role.charAt(0).toUpperCase() + role.slice(1)}
                  checked={editModal.formData.roles.includes(role)}
                  onChange={() => handleRoleToggle(role)}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal
        isOpen={showInvitationsModal}
        title="Invitations Sent"
        onClose={() => setShowInvitationsModal(false)}
        showConfirm={false}
        cancelLabel="Close"
        loading={loadingInvitations}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {invitations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography fontSize="0.875rem" style={{ color: '#6b7280' }}>
                No pending invitations
              </Typography>
            </Box>
          ) : (
            invitations.map(invitation => (
              <InvitationRow key={invitation.id}>
                <InvitationInfo>
                  <InvitationName>
                    {invitation.firstName} {invitation.lastName}
                  </InvitationName>
                  <InvitationEmail>{invitation.email}</InvitationEmail>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 0.25,
                    }}
                  >
                    <RoleTag role={invitation.role}>
                      {(invitation.role || '').charAt(0).toUpperCase() +
                        (invitation.role || '').slice(1)}
                    </RoleTag>
                    <InvStatusBadge status={invitation.status}>
                      {invitation.status}
                    </InvStatusBadge>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                    <Typography
                      fontSize="0.625rem"
                      style={{ color: '#9ca3af' }}
                    >
                      Sent: {formatDate(invitation.sentAt)}
                    </Typography>
                    <Typography
                      fontSize="0.625rem"
                      style={{ color: '#9ca3af' }}
                    >
                      Expires: {formatDate(invitation.expiresAt)}
                    </Typography>
                  </Box>
                </InvitationInfo>
                <InvitationActions>
                  <ActionButton
                    onClick={() => handleEditInvitation(invitation)}
                  >
                    Edit
                  </ActionButton>
                  <ActionButton
                    onClick={() => handleResendInvitation(invitation)}
                  >
                    Resend
                  </ActionButton>
                  <ActionButton
                    $variant="danger"
                    onClick={() => handleDeleteInvitation(invitation)}
                  >
                    Delete
                  </ActionButton>
                </InvitationActions>
              </InvitationRow>
            ))
          )}
        </Box>
      </Modal>

      <Modal
        isOpen={showEditInvitationModal}
        title="Edit Invitation"
        onClose={() => {
          setShowEditInvitationModal(false)
          setEditingInvitation(null)
        }}
        onConfirm={handleEditInvitationSubmit}
        confirmLabel="Save"
        cancelLabel="Cancel"
        submitDisabled={isUpdatingInvitation}
        loading={isUpdatingInvitation}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography bold fontSize="1rem">
              Invitation Details
            </Typography>
            <Typography
              fontSize="0.875rem"
              style={{ color: '#6b7280', marginBottom: '1rem' }}
            >
              Update the invitation details before the recipient accepts
            </Typography>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}
            >
              <TextField
                fullWidth
                label="Name"
                value={editInvitationFormData.firstName}
                onChange={val =>
                  handleEditInvitationInputChange('firstName', val)
                }
              />
              <TextField
                fullWidth
                label="Last Name"
                value={editInvitationFormData.lastName}
                onChange={val =>
                  handleEditInvitationInputChange('lastName', val)
                }
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editInvitationFormData.email}
                onChange={val => handleEditInvitationInputChange('email', val)}
              />
            </Box>
          </Box>

          <Box>
            <Typography bold fontSize="1rem">
              Role
            </Typography>
            <Typography
              fontSize="0.875rem"
              style={{ color: '#6b7280', marginBottom: '1rem' }}
            >
              Choose which role the invited member will have
            </Typography>
            <SelectField
              fullWidth
              label="Role"
              options={roleOptions}
              value={editInvitationFormData.role}
              onChange={val => handleEditInvitationInputChange('role', val)}
            />
          </Box>
        </Box>
      </Modal>
    </SettingsPage>
  )
}

export default Users
